/**
 * Calls page ← Zoho, through n8n "[C] App 8 - Calls" (REACT_APP_CALLS_URL).
 *
 * Sections (agreed 2026-10-21):
 *   needsQuote  Inspection: Waiting for Report/Quote          noShow  Inspection: Rebooking / Cancelled
 *   followUp    Deal: Report/Quote Sent / Follow Up — sub-tabs from its open "Follow-up call" Task:
 *                 due      call date today / overdue, or no call set yet
 *                 upcoming call date later
 *   booked      Deal: Closed Won                               dismissed  Deal: Closed Lost (+ Lost Reason)
 *   ⭐ Priority  the "Priority" tag, on any of them
 *
 * Actions write straight to Zoho (Tasks, Notes, Deal stage, tags) and then reload the board.
 * Quotes come from the Zoho Quotes (App 6 read), photos from the shared sent inspection.
 */
import { useSyncExternalStore } from 'react';
import { AUTH_PROVIDER, getAccessToken } from './auth';
import { callQuotes } from './quoteSend';
import { localDate } from './dealOutcome';

const CALLS_URL = process.env.REACT_APP_CALLS_URL;
export const CALLS_LIVE = !!CALLS_URL && AUTH_PROVIDER === 'supabase' && !!process.env.REACT_APP_LEADS_URL;

export const LOST_REASONS = ['Price', 'Went with another company', 'Not ready / timing', 'No response', 'Not a good candidate', 'Other'];

/* ---------- talking to n8n ---------- */
export const callBoard = async (body) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to reach Zoho.');
  let res;
  try {
    res = await fetch(CALLS_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error('No connection — try again when you have signal.');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || `Zoho didn’t answer (${res.status}).`);
  return out;
};

/* ---------- the board ---------- */
let items = [];
let load = { status: 'idle', error: '', fetchedAt: null };
const details = new Map(); // id → { quotes, notes, loading, error }
const listeners = new Set();
let snapshot = { items, load };
const emit = () => {
  snapshot = { items, load };
  listeners.forEach((fn) => fn());
};
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// due vs upcoming: a call set for today or earlier (or none at all) is due
export const followUpTab = (item, today = localDate(new Date())) =>
  item.nextCall && item.nextCall.dueDate > today ? 'upcoming' : 'due';

let inFlight = null;
export const refreshCalls = () => {
  if (!CALLS_LIVE) return Promise.resolve(items);
  if (inFlight) return inFlight;
  load = { ...load, status: 'loading', error: '' };
  emit();
  inFlight = callBoard({ action: 'list' })
    .then((out) => {
      items = out.items || [];
      load = { status: 'ready', error: '', fetchedAt: out.fetchedAt };
    })
    .catch((err) => {
      load = { ...load, status: 'error', error: err.message };
    })
    .finally(() => {
      inFlight = null;
      emit();
    });
  return inFlight;
};

export const useCalls = () => useSyncExternalStore(subscribe, () => snapshot);
export const getCallItem = (id) => items.find((i) => i.id === id) || null;

/* ---------- one customer: quotes + notes (+ photos from the sent inspection) ---------- */
const HST = 0.13;
const r2 = (n) => Math.round(n * 100) / 100;
// Zoho's copy of a quote → the shape the detail page shows
const toQuote = (q) => ({
  zohoId: q.zohoId || null,
  stage: q.stage || '',
  number: q.quoteNumber || 'Quote',
  name: String(q.subject || '').split(' — ')[0] || 'Quote',
  discount: q.discount ? { name: q.discountName || 'Discount', type: 'amount', value: q.discount } : null,
  lines: (q.items || []).map((l) => ({
    category: 'Services',
    name: l.name || 'Service',
    description: l.description || '',
    qty: l.quantity,
    unit: 'ea',
    rate: l.rate,
  })),
});
export const zohoQuoteTotal = (q) => {
  const sub = (q.lines || []).reduce((s, l) => s + l.qty * l.rate, 0);
  const disc = q.discount ? q.discount.value : 0;
  return r2((sub - disc) * (1 + HST));
};

export const useCallDetail = (id) => {
  useSyncExternalStore(subscribe, () => snapshot);
  return details.get(id) || null;
};

export const loadDetail = async (item) => {
  const prev = details.get(item.id) || {};
  details.set(item.id, { ...prev, loading: true, error: '' });
  emit();
  try {
    const [notesOut, quotes] = await Promise.all([
      callBoard({ action: 'detail', dealId: item.dealId, inspectionId: item.inspectionId }),
      item.inspectionId && item.kind === 'deal'
        ? callQuotes({ action: 'read', inspectionId: item.inspectionId }).then((o) => (o.quotes || []).map(toQuote)).catch(() => [])
        : Promise.resolve([]),
    ]);
    const notes = (notesOut.notes || [])
      .map((n) => {
        // App notes read "Name - text"; others show their Zoho owner
        const m = /^([^-\n]{1,40}) - ([\s\S]*)$/.exec(n.text || '');
        return { id: n.id, author: m ? m[1] : n.owner || 'Zoho', text: m ? m[2] : n.text, title: n.title, at: n.at };
      })
      .sort((a, b) => String(a.at).localeCompare(String(b.at)));
    details.set(item.id, { quotes, notes, loading: false, error: '' });
  } catch (err) {
    details.set(item.id, { ...prev, loading: false, error: err.message });
  }
  emit();
};

/* ---------- actions ---------- */
const target = (item) => ({ dealId: item.dealId || undefined, inspectionId: item.inspectionId || undefined });
const act = async (item, body) => {
  const out = await callBoard({ ...target(item), ...body });
  await refreshCalls();
  if (details.has(item.id)) loadDetail(getCallItem(item.id) || item);
  return out;
};

export const addCallNote = (item, note) => {
  if (!String(note || '').trim()) throw new Error('Write a note first');
  return act(item, { action: 'note', note: note.trim() });
};
// the picked quote → Confirmed, the deal's other quotes → Closed Lost; the deal is tagged "Invoice not sent"
export const bookCall = (item, quote, note = '', allQuotes = []) =>
  act(item, {
    action: 'book',
    package: { name: quote.name, total: zohoQuoteTotal(quote), quoteId: quote.zohoId },
    otherQuoteIds: allQuotes.map((q) => q.zohoId).filter((id) => id && id !== quote.zohoId),
    note,
  });

/* ---------- invoice (n8n "[C] Invoice 1 - Send", REACT_APP_INVOICE_URL) ---------- */
// Books invoice from the quote: emailed from support@, pay link texted, put on the Job. Takes ~20 s.
const INVOICE_URL = process.env.REACT_APP_INVOICE_URL;
export const INVOICE_LIVE = !!INVOICE_URL;
// the quote the invoice is built from: the Confirmed one, else the highest
export const invoiceQuoteIdx = (quotes = []) => {
  const confirmed = quotes.findIndex((q) => q.stage === 'Confirmed');
  if (confirmed >= 0) return confirmed;
  return quotes.reduce((best, q, i) => (zohoQuoteTotal(q) > zohoQuoteTotal(quotes[best]) ? i : best), 0);
};
export const sendInvoice = async (item, quote) => {
  if (!INVOICE_LIVE) throw new Error('Invoices aren’t switched on in this version');
  if (!quote || !quote.zohoId) throw new Error('Pick the quote first');
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to reach Zoho.');
  let res;
  try {
    res = await fetch(INVOICE_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ dealId: item.dealId, quoteId: quote.zohoId }),
    });
  } catch (e) {
    throw new Error('No connection — try again when you have signal.');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || `The invoice didn’t go out (${res.status}).`);
  await refreshCalls();
  if (details.has(item.id)) loadDetail(getCallItem(item.id) || item);
  return out;
};
export const dismissCall = (item, lostReason, note = '') => {
  if (!LOST_REASONS.includes(lostReason)) throw new Error('Pick why it was lost');
  return act(item, { action: 'dismiss', lostReason, note });
};
export const scheduleCall = (item, dueDate, note = '') => {
  if (!dueDate) throw new Error('Pick a date');
  return act(item, { action: 'follow_up', dueDate, note });
};
export const noAnswerCall = (item) => act(item, { action: 'no_answer' });
export const officeTaskCall = (item, dueDate, note) => act(item, { action: 'office_task', dueDate, note });
export const togglePriority = (item) => {
  // show it straight away; the reload confirms
  items = items.map((i) => (i.id === item.id ? { ...i, priority: !item.priority } : i));
  emit();
  return act(item, { action: 'priority', on: !item.priority });
};
